import { EQUALITY_TOLERANCE, nearlyEqual } from '@/lib/metrics';
import type { DrinkCategory } from '@/types/data';

export interface RankableDrink {
  id: string;
  name: string;
  category: DrinkCategory;
  subtype: string;
  alcoholPer100Calories: number | null;
  alcoholPerDollar: number | null;
  caloriesPerStandardDrink: number | null;
  dollarsPerStandardDrink: number | null;
}

export type ActiveRatio = 'alcoholPer100Calories' | 'alcoholPerDollar';

export type CombinedBucket =
  | 'lower-cal-lower-cost'
  | 'lower-cal-higher-cost'
  | 'higher-cal-lower-cost'
  | 'higher-both'
  | 'ties'
  | 'insufficient';

function ratioValue(d: RankableDrink, key: ActiveRatio): number | null {
  return key === 'alcoholPer100Calories'
    ? d.alcoholPer100Calories
    : d.alcoholPerDollar;
}

export function filterByScope(
  drinks: RankableDrink[],
  baseline: RankableDrink,
  scope: 'subtype' | 'category' | 'all',
): RankableDrink[] {
  return drinks.filter((d) => {
    if (d.id === baseline.id) return false;
    if (scope === 'all') return true;
    if (scope === 'category') return d.category === baseline.category;
    return d.category === baseline.category && d.subtype === baseline.subtype;
  });
}

/** Nearby better/worse along the active ratio. Higher is better. */
export function nearbyAlternatives(
  drinks: RankableDrink[],
  baseline: RankableDrink,
  activeRatio: ActiveRatio,
  limit = 5,
): { better: RankableDrink[]; worse: RankableDrink[] } {
  const baseVal = ratioValue(baseline, activeRatio);
  if (baseVal == null) return { better: [], worse: [] };

  const withValues = drinks
    .map((d) => ({ d, v: ratioValue(d, activeRatio) }))
    .filter((x): x is { d: RankableDrink; v: number } => x.v != null);

  const better = withValues
    .filter((x) => x.v > baseVal + EQUALITY_TOLERANCE)
    .sort((a, b) => a.v - b.v || a.d.name.localeCompare(b.d.name))
    .slice(0, limit)
    .map((x) => x.d);

  const worse = withValues
    .filter((x) => x.v < baseVal - EQUALITY_TOLERANCE)
    .sort((a, b) => b.v - a.v || a.d.name.localeCompare(b.d.name))
    .slice(0, limit)
    .map((x) => x.d);

  return { better, worse };
}

export function bestInCategory(
  drinks: RankableDrink[],
  baseline: RankableDrink,
  activeRatio: ActiveRatio,
  limit = 5,
): RankableDrink[] {
  const peers = drinks.filter(
    (d) => d.category === baseline.category && d.id !== baseline.id,
  );
  return [...peers]
    .filter((d) => ratioValue(d, activeRatio) != null)
    .sort((a, b) => {
      const av = ratioValue(a, activeRatio)!;
      const bv = ratioValue(b, activeRatio)!;
      if (bv !== av) return bv - av;
      return a.name.localeCompare(b.name);
    })
    .slice(0, limit);
}

/**
 * Better on both: no worse on either metric, strictly better on at least one.
 * Comparisons use calories/std and dollars/std (lower better).
 */
export function isBetterOnBoth(
  candidate: RankableDrink,
  baseline: RankableDrink,
  tolerance = 1e-6,
): boolean {
  const cCal = candidate.caloriesPerStandardDrink;
  const bCal = baseline.caloriesPerStandardDrink;
  const cPrice = candidate.dollarsPerStandardDrink;
  const bPrice = baseline.dollarsPerStandardDrink;
  if (cCal == null || bCal == null || cPrice == null || bPrice == null) {
    return false;
  }
  const calOk = cCal <= bCal + tolerance;
  const priceOk = cPrice <= bPrice + tolerance;
  const strictlyBetter =
    cCal < bCal - tolerance || cPrice < bPrice - tolerance;
  return calOk && priceOk && strictlyBetter;
}

export function combinedBucket(
  candidate: RankableDrink,
  baseline: RankableDrink,
  tolerance = 1e-6,
): CombinedBucket {
  const cCal = candidate.caloriesPerStandardDrink;
  const bCal = baseline.caloriesPerStandardDrink;
  const cPrice = candidate.dollarsPerStandardDrink;
  const bPrice = baseline.dollarsPerStandardDrink;
  if (cCal == null || bCal == null || cPrice == null || bPrice == null) {
    return 'insufficient';
  }

  const calLower = cCal < bCal - tolerance;
  const calHigher = cCal > bCal + tolerance;
  const priceLower = cPrice < bPrice - tolerance;
  const priceHigher = cPrice > bPrice + tolerance;
  const calTie = nearlyEqual(cCal, bCal, tolerance);
  const priceTie = nearlyEqual(cPrice, bPrice, tolerance);

  if (calTie && priceTie) return 'ties';
  if (calLower && priceLower) return 'lower-cal-lower-cost';
  if (calLower && (priceHigher || priceTie)) return 'lower-cal-higher-cost';
  if ((calHigher || calTie) && priceLower) return 'higher-cal-lower-cost';
  if (calHigher && priceHigher) return 'higher-both';
  if (isBetterOnBoth(candidate, baseline, tolerance)) {
    return 'lower-cal-lower-cost';
  }
  return 'ties';
}

export function groupCombinedView(
  drinks: RankableDrink[],
  baseline: RankableDrink,
): Record<CombinedBucket, RankableDrink[]> {
  const groups: Record<CombinedBucket, RankableDrink[]> = {
    'lower-cal-lower-cost': [],
    'lower-cal-higher-cost': [],
    'higher-cal-lower-cost': [],
    'higher-both': [],
    ties: [],
    insufficient: [],
  };
  for (const d of drinks) {
    if (d.id === baseline.id) continue;
    groups[combinedBucket(d, baseline)].push(d);
  }
  return groups;
}
